
import { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Checkbox,
  FormControlLabel,
  Switch,
  Snackbar
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import { APIHOST } from '../../common';
import { Conference } from '../../types/conference';
import { ConferenceHotelRoom, ConferenceHotelRoomResponse } from '../../types/conferenceHotelRoom';
import { Hotel } from '../../types/hotel';
import { ConferenceGroup, ConferenceGroupResponse } from '../../types/conferenceGroup';
import { ConferenceHotel } from '../../types/conferenceHotel';
import EditConferenceDialog from './EditConferenceDialog';
import EditGroupDialog from './EditGroupDialog';
import EditConferenceHotelDialog from './EditConferenceHotelDialog';

interface ConferenceDetailProps {
  conferenceId: string;
}

export default function ConferenceDetail({ conferenceId }: ConferenceDetailProps) {
  const [conference, setConference] = useState<Conference | null>(null);
  const [rooms, setRooms] = useState<ConferenceHotelRoom[]>([]);
  const [conferenceHotels, setConferenceHotels] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [error, setError] = useState('');
  const [roomsError, setRoomsError] = useState('');
  const [showRooms, setShowRooms] = useState(false);
  const [showAddRoomDialog, setShowAddRoomDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    dailyRate: '',
    blockTotal: '',
    maxOccupants: '',
    bedCount: '1',
    conference_hotel: ''
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [editingRoom, setEditingRoom] = useState<ConferenceHotelRoom | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showAddHotelDialog, setShowAddHotelDialog] = useState(false);
  const [hotelDialogTab, setHotelDialogTab] = useState(0);
  const [availableHotels, setAvailableHotels] = useState<Hotel[]>([]);
  const [selectedHotels, setSelectedHotels] = useState<string[]>([]);
  const [newHotelData, setNewHotelData] = useState({
    name: '',
    longName: '',
    addressCity: '',
    addressState: '',
    addressZip: '',
    website: '',
    amenities: '',
    contactName: '',
    contactEmail: ''
  });
  const [hotelFormLoading, setHotelFormLoading] = useState(false);
  const [hotelFormError, setHotelFormError] = useState('');
  const [showEditConferenceDialog, setShowEditConferenceDialog] = useState(false);
  const [hotelTabs, setHotelTabs] = useState<Record<string, number>>({});
  const [mainTab, setMainTab] = useState(0); // 0: Blocks, 1: Hotels, 2: Groups
  const [conferenceGroups, setConferenceGroups] = useState<ConferenceGroup[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsError, setGroupsError] = useState('');
  const [showAddGroupDialog, setShowAddGroupDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState<ConferenceGroup | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [showEditConferenceHotelDialog, setShowEditConferenceHotelDialog] = useState(false);
  const [editingConferenceHotel, setEditingConferenceHotel] = useState<ConferenceHotel | null>(null);

  const handleHotelTabChange = (hotelId: string, newValue: number) => {
    setHotelTabs(prev => ({ ...prev, [hotelId]: newValue }));
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleHotelTierChange = async (hotelId: string, isTierB: boolean) => {
    try {

      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Update conference hotel tier via API
      const response = await fetch(`${APIHOST}/api/conference-hotels/${hotelId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          data: {
            tier: isTierB ? 'B' : 'A'
          }
        }),
      });

      fetchConferenceHotels();

      if (!response.ok) {
        throw new Error('Failed to update hotel tier');
      }

      console.log(`Hotel ${hotelId} tier updated to: ${isTierB ? 'Tier B' : 'Tier A'}`);
      
      // Show success snackbar
      setSnackbarMessage('Hotel tier updated');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error updating hotel tier:', err);

      // Show error snackbar
      setSnackbarMessage('Hotel tier update failed');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleHotelVisibilityChange = async (hotelId: string, isHidden: boolean) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Update conference hotel visibility via API
      const response = await fetch(`${APIHOST}/api/conference-hotels/${hotelId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          data: {
            visible: !isHidden
          }
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update hotel visibility');
      }

      fetchConferenceHotels();

      console.log(`Hotel ${hotelId} visibility updated to: ${isHidden ? 'Hidden' : 'Visible'}`);
      
      // Show success snackbar
      setSnackbarMessage('Hotel visibility updated');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error updating hotel visibility:', err);

      // Show error snackbar
      setSnackbarMessage('Hotel visibility update failed');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  useEffect(() => {
    if (conferenceId) {
      fetchConference();
      fetchConferenceRooms();
      fetchConferenceHotels();
      fetchConferenceGroups();
    }
  }, [conferenceId]);

  const fetchConference = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(
        `${APIHOST}/api/conferences/${conferenceId}?populate=*`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch conference details');
      }

      const data = await response.json();
      setConference(data.data);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load conference details');
      console.error('Conference detail error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchConferenceRooms = async () => {
    try {
      setRoomsLoading(true);
      setRoomsError('');

      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      let allRooms: any[] = [];
      let currentPage = 1;
      let totalPages = 1;

      // Fetch all pages of conference hotel rooms
      do {
        const response = await fetch(
          `${APIHOST}/api/conference-hotel-rooms?filters[visibility][$eq]=visible&filters[conference_hotel][conference][documentId][$eq]=${conferenceId}&populate=conference_hotel.hotel&sort=name:asc&pagination[page]=${currentPage}&pagination[pageSize]=25`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch conference rooms');
        }

        const data: ConferenceHotelRoomResponse = await response.json();
        
        // Add rooms from this page to our collection
        if (data.data) {
          allRooms = [...allRooms, ...data.data];
        }

        // Update pagination info from first response
        if (currentPage === 1 && data.meta?.pagination) {
          totalPages = data.meta.pagination.pageCount;
        }

        currentPage++;
      } while (currentPage <= totalPages);

      setRooms(allRooms);

    } catch (err) {
      setRoomsError(err instanceof Error ? err.message : 'Failed to load conference rooms');
      console.error('Conference rooms error:', err);
    } finally {
      setRoomsLoading(false);
    }
  };

  const fetchConferenceHotels = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(
        `${APIHOST}/api/conference-hotels?filters[conference][documentId][$eq]=${conferenceId}&populate=hotel&pagination[pageSize]=100&pagination[page]=1&sort=hotel.longName:asc`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setConferenceHotels(data.data || []);
      }
    } catch (err) {
      console.error('Error fetching conference hotels:', err);
    }
  };

  const fetchConferenceGroups = async () => {
    try {
      setGroupsLoading(true);
      setGroupsError('');

      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(
        `${APIHOST}/api/conference-groups?filters[conference][documentId][$eq]=${conferenceId}&sort=name:asc`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch conference groups');
      }

      const data: ConferenceGroupResponse = await response.json();
      setConferenceGroups(data.data || []);

    } catch (err) {
      setGroupsError(err instanceof Error ? err.message : 'Failed to load conference groups');
      console.error('Error fetching conference groups:', err);
    } finally {
      setGroupsLoading(false);
    }
  };

  const fetchAvailableHotels = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(
        `${APIHOST}/api/hotels?sort=longName:asc`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const allHotels = data.data || [];
        
        // Filter out hotels already associated with this conference
        const existingHotelIds = conferenceHotels.map(ch => ch.hotel.documentId);
        const available = allHotels.filter((hotel: ConferenceHotel) => !existingHotelIds.includes(hotel.documentId));
        setAvailableHotels(available);
      }
    } catch (err) {
      console.error('Error fetching available hotels:', err);
    }
  };

  const handleCloseConferenceHotelDialog = () => {
    setShowEditConferenceHotelDialog(false);
    setEditingConferenceHotel(null);
  };

  const handleConferenceHotelUpdated = async () => {
    await Promise.all([fetchConferenceHotels(), fetchConferenceRooms()]);
  };

  const handleEditGroup = (group: ConferenceGroup) => {
    setEditingGroup(group);
    setShowAddGroupDialog(true);
  };

  const handleAddGroup = () => {
    setEditingGroup(null);
    setShowAddGroupDialog(true);
  };

  const handleCloseGroupDialog = () => {
    setShowAddGroupDialog(false);
    setEditingGroup(null);
  };

  const handleGroupUpdated = async () => {
    await fetchConferenceGroups();
  };

  const handleAddExistingHotels = async () => {
    try {
      setHotelFormLoading(true);
      setHotelFormError('');

      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      if (selectedHotels.length === 0) {
        throw new Error('Please select at least one hotel');
      }

      // Create conference-hotel associations for each selected hotel
      const promises = selectedHotels.map(hotelId => 
        fetch(`${APIHOST}/api/conference-hotels`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            data: {
              conference: conferenceId,
              hotel: hotelId
            }
          })
        })
      );

      const responses = await Promise.all(promises);
      const failedRequests = responses.filter(r => !r.ok);
      
      if (failedRequests.length > 0) {
        throw new Error(`Failed to add ${failedRequests.length} hotel(s)`);
      }

      // Reset and refresh
      setSelectedHotels([]);
      setShowAddHotelDialog(false);
      await Promise.all([fetchConferenceHotels(), fetchConferenceRooms()]);

    } catch (err) {
      setHotelFormError(err instanceof Error ? err.message : 'Failed to add hotels');
    } finally {
      setHotelFormLoading(false);
    }
  };

  const handleCreateNewHotel = async () => {
    try {
      setHotelFormLoading(true);
      setHotelFormError('');

      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      if (!newHotelData.name || !newHotelData.longName) {
        throw new Error('Hotel name and full name are required');
      }

      // First create the hotel
      const hotelResponse = await fetch(`${APIHOST}/api/hotels`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            name: newHotelData.name,
            longName: newHotelData.longName,
            addressCity: newHotelData.addressCity || null,
            addressState: newHotelData.addressState || null,
            addressZip: newHotelData.addressZip || null,
            website: newHotelData.website || null,
            amenities: newHotelData.amenities || null,
            contactName: newHotelData.contactName || null,
            contactEmail: newHotelData.contactEmail || null
          }
        })
      });

      if (!hotelResponse.ok) {
        throw new Error('Failed to create hotel');
      }

      const hotelData = await hotelResponse.json();

      // Then associate it with the conference
      const conferenceHotelResponse = await fetch(`${APIHOST}/api/conference-hotels`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            conference: conferenceId,
            hotel: hotelData.data.documentId
          }
        })
      });

      if (!conferenceHotelResponse.ok) {
        throw new Error('Failed to associate hotel with conference');
      }

      // Reset and refresh
      setNewHotelData({
        name: '',
        longName: '',
        addressCity: '',
        addressState: '',
        addressZip: '',
        website: '',
        amenities: '',
        contactName: '',
        contactEmail: ''
      });
      setShowAddHotelDialog(false);
      await Promise.all([fetchConferenceHotels(), fetchConferenceRooms()]);

    } catch (err) {
      setHotelFormError(err instanceof Error ? err.message : 'Failed to create hotel');
    } finally {
      setHotelFormLoading(false);
    }
  };

  const handleCloseHotelDialog = () => {
    setShowAddHotelDialog(false);
    setHotelDialogTab(0);
    setHotelFormError('');
    setSelectedHotels([]);
    setNewHotelData({
      name: '',
      longName: '',
      addressCity: '',
      addressState: '',
      addressZip: '',
      website: '',
      amenities: '',
      contactName: '',
      contactEmail: ''
    });
  };

  const handleEditConference = () => {
    setShowEditConferenceDialog(true);
  };

  const handleCloseEditConferenceDialog = () => {
    setShowEditConferenceDialog(false);
  };

  const handleConferenceUpdated = async () => {
    await fetchConference();
  };

  const handleEditRoom = (room: ConferenceHotelRoom) => {
    setEditingRoom(room);
    setIsEditMode(true);
    setFormData({
      name: room.name,
      dailyRate: room.dailyRate.toString(),
      blockTotal: room.blockTotal.toString(),
      maxOccupants: room.maxOccupants?.toString() || '',
      bedCount: room.bedCount?.toString() || '1',
      conference_hotel: room.conference_hotel?.documentId || ''
    });
    setShowAddRoomDialog(true);
  };

  const handleDeleteRoom = async (room: ConferenceHotelRoom) => {
    if (!window.confirm(`Are you sure you want to delete "${room.name}"? This will hide the room from availability.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${APIHOST}/api/conference-hotel-rooms/${room.documentId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            visibility: 'hidden'
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to delete room');
      }

      // Show success message
      setSnackbarMessage('Room deleted successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      // Refresh rooms list
      await fetchConferenceRooms();

    } catch (err) {
      console.error('Error deleting room:', err);
      setSnackbarMessage('Failed to delete room. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleFormSubmit = async () => {
    try {
      setFormLoading(true);
      setFormError('');

      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Validate required fields
      if (!formData.name || !formData.dailyRate || !formData.blockTotal || !formData.conference_hotel) {
        throw new Error('Please fill in all required fields');
      }

      const method = isEditMode ? 'PUT' : 'POST';
      const url = isEditMode 
        ? `${APIHOST}/api/conference-hotel-rooms/${editingRoom?.documentId}`
        : `${APIHOST}/api/conference-hotel-rooms`;

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            name: formData.name,
            dailyRate: parseFloat(formData.dailyRate),
            blockTotal: parseInt(formData.blockTotal),
            maxOccupants: formData.maxOccupants ? parseInt(formData.maxOccupants) : null,
            bedCount: parseInt(formData.bedCount),
            conference_hotel: formData.conference_hotel
          }
        })
      });

      if (!response.ok) {
        throw new Error(isEditMode ? 'Failed to update room' : 'Failed to create room');
      }

      // Reset form and close dialog
      setFormData({
        name: '',
        dailyRate: '',
        blockTotal: '',
        maxOccupants: '',
        bedCount: '1',
        conference_hotel: ''
      });
      setIsEditMode(false);
      setEditingRoom(null);
      setShowAddRoomDialog(false);
      
      // Refresh rooms list
      await fetchConferenceRooms();

    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create room');
    } finally {
      setFormLoading(false);
    }
  };

  const handleFormChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCloseDialog = () => {
    setShowAddRoomDialog(false);
    setFormError('');
    setIsEditMode(false);
    setEditingRoom(null);
    setFormData({
      name: '',
      dailyRate: '',
      blockTotal: '',
      maxOccupants: '',
      bedCount: '1',
      conference_hotel: ''
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString+`T00:00:00`).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getConferenceStatus = (startDate: string, endDate: string) => {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (now < start) return { label: 'Upcoming', color: 'primary' as const };
    if (now >= start && now <= end) return { label: 'Active', color: 'success' as const };
    return { label: 'Completed', color: 'default' as const };
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="body1">Loading conference details...</Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  if (!conference) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="warning">Conference not found</Alert>
      </Paper>
    );
  }

  const status = getConferenceStatus(conference.defaultArrivalDate, conference.defaultDepartureDate);

  return (
    <Box>
      {/* Header Section with Conference Details */}
      <Paper sx={{ p: 4, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
          {conference.logo && (
            <Avatar
              src={conference.logo.url}
              alt={conference.logo.alternativeText || conference.longName}
              sx={{ width: 80, height: 80 }}
            />
          )}
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Typography variant="h4" component="h1" gutterBottom>
                {conference.longName}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleEditConference}
                >
                  Edit
                </Button>
                <Chip 
                  label={status.label}
                  color={status.color}
                  size="medium"
                />
              </Box>
            </Box>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {conference.name}
            </Typography>
            
            <Divider sx={{ my: 2 }} />
            
            {/* Conference Details moved to header */}
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  Conference Dates
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatDate(conference.defaultArrivalDate)} - {formatDate(conference.defaultDepartureDate)}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  Earliest Arrival
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatDate(conference.earliestArrivalDate)}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Paper>

      {/* Logo Information */}
      {conference.logo && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Conference Logo
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2} alignItems="center">
                  <Grid>
                    <Avatar
                      src={conference.logo.url}
                      alt={conference.logo.alternativeText || conference.longName}
                      sx={{ width: 120, height: 120 }}
                      variant="rounded"
                    />
                  </Grid>
                  <Grid>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        File Name
                      </Typography>
                      <Typography variant="body1">
                        {conference.logo.name}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Dimensions
                      </Typography>
                      <Typography variant="body1">
                        {conference.logo.width} × {conference.logo.height} pixels
                      </Typography>
                    </Box>
                    
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        File Size
                      </Typography>
                      <Typography variant="body1">
                        {(conference.logo.size / 1024).toFixed(1)} KB
                      </Typography>
                    </Box>

                    {conference.logo.alternativeText && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Alt Text
                        </Typography>
                        <Typography variant="body1">
                          {conference.logo.alternativeText}
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Conference Hotels/Groups/Blocks Section */}
      <Paper sx={{ p: 3, mt:  3}}>

        <Tabs value={mainTab} onChange={(e, newValue) => setMainTab(newValue)} sx={{ mb: 3 }}>
          <Tab label="Hotels" />
          <Tab label="Groups" />
        </Tabs>

        {/* Hotels Tab */}
        {mainTab === 0 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    setEditingConferenceHotel(null);
                    setShowEditConferenceHotelDialog(true);
                  }}
                >
                  Add Hotel to Conference
                </Button>
              </Box>
            </Box>

        {roomsLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading rooms...
            </Typography>
          </Box>
        )}

        {roomsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {roomsError}
          </Alert>
        )}

        {conferenceHotels.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            No hotels associated with this conference
          </Typography>
        ) : (
          <Box>
            {conferenceHotels.map((conferenceHotel) => {
              // Get rooms for this specific hotel
              const hotelRooms = rooms.filter(room => 
                room.conference_hotel?.documentId === conferenceHotel.documentId
              );

              return (
                <Card key={conferenceHotel.documentId} sx={{ m: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          {conferenceHotel.hotel.longName}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexDirection: 'row' }}>
                          <Typography variant="body2" color="text.secondary">
                            Tier A
                          </Typography>
                          <Switch
                            checked={conferenceHotel.tier === 'B'}
                            onChange={(e) => handleHotelTierChange(conferenceHotel.documentId, e.target.checked)}
                            color={"default"}
                          />
                          <Typography variant="body2" color="text.secondary">
                            Tier B
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexDirection: 'row' }}>
                          <Typography variant="body2" color="text.secondary">
                            Visible
                          </Typography>
                          <Switch
                            checked={conferenceHotel.visible === false}
                            onChange={(e) => handleHotelVisibilityChange(conferenceHotel.documentId, e.target.checked)}
                            color={conferenceHotel.visible === false ? "error" : "default"}
                          />
                          <Typography variant="body2" color="text.secondary">
                            Hidden
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Hotel Tabs */}
                    <Tabs 
                      value={hotelTabs[conferenceHotel.documentId] || 0} 
                      onChange={(e, newValue) => handleHotelTabChange(conferenceHotel.documentId, newValue)}
                      sx={{ mb: 3 }}
                    >
                      <Tab label="Rooms" />
                      <Tab label="Details" />
                    </Tabs>

                    {/* Rooms Tab Content */}
                    {(hotelTabs[conferenceHotel.documentId] || 0) === 0 && (
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6" sx={{ color: 'primary.main' }}>
                            Available Rooms ({hotelRooms.length})
                          </Typography>
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() => {
                              // Pre-select this hotel in the form
                              setFormData(prev => ({ ...prev, conference_hotel: conferenceHotel.documentId }));
                              setShowAddRoomDialog(true);
                            }}
                          >
                            Add Room Type
                          </Button>
                        </Box>
                    
                    {!roomsLoading && hotelRooms.length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No rooms available for this hotel
                      </Typography>
                    )}
                    
                    {hotelRooms.length > 0 && (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell><strong>Room Name</strong></TableCell>
                              <TableCell align="right"><strong>Daily Rate</strong></TableCell>
                              <TableCell align="center"><strong>Allocated</strong></TableCell>
                              <TableCell align="center"><strong>Reserved</strong></TableCell>
                              <TableCell align="center"><strong>Remaining</strong></TableCell>
                              <TableCell align="center"><strong>Max Occupants</strong></TableCell>
                              <TableCell align="center"><strong>Beds</strong></TableCell>
                              <TableCell align="center"><strong>Images</strong></TableCell>
                              <TableCell align="center"><strong>Actions</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {hotelRooms.map((room: ConferenceHotelRoom) => (
                              <TableRow key={room.documentId} hover>
                                <TableCell>
                                  <Typography variant="body2">
                                    {room.name}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  {formatCurrency(room.dailyRate)}
                                  
                                </TableCell>

                                <TableCell align="center">
                                  <Chip 
                                    label={room.blockTotal}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Chip 
                                    label={room.reservedTotal || 0}
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Chip 
                                    label={room.remainingAvailable || "SOLD OUT"}
                                    size="small"
                                    color={room.remainingAvailable > 0 ? "success" : "error"}
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="body2">
                                    {room.maxOccupants || 'N/A'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="body2">
                                    {room.bedCount || '?'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  {room.pics && room.pics.length > 0 ? (
                                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                      {room.pics.slice(0, 3).map((pic, index) => (
                                        <Avatar
                                          key={pic.id}
                                          src={pic.url}
                                          alt={pic.alternativeText || room.name}
                                          sx={{ width: 32, height: 32 }}
                                          variant="rounded"
                                        />
                                      ))}
                                      {room.pics.length > 3 && (
                                        <Chip
                                          label={`+${room.pics.length - 3}`}
                                          size="small"
                                          sx={{ height: 32, fontSize: '0.7rem' }}
                                        />
                                      )}
                                    </Box>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      No images
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell align="center">
                                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={() => handleEditRoom(room)}
                                    >
                                      Edit
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      color="error"
                                      onClick={() => handleDeleteRoom(room)}
                                    >
                                      Delete
                                    </Button>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                      </Box>
                    )}

                    {/* Details Tab Content */}
                    {(hotelTabs[conferenceHotel.documentId] || 0) === 1 && (
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                          <Typography variant="h6" sx={{ color: 'primary.main' }}>
                            Hotel Configuration
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              setEditingConferenceHotel(conferenceHotel);
                              setShowEditConferenceHotelDialog(true);
                            }}
                          >
                            Edit Hotel Details
                          </Button>
                        </Box>
                        
                        <Grid container spacing={3}>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="subtitle1" gutterBottom>
                              Transportation & Logistics
                            </Typography>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                Bus Parking Available
                              </Typography>
                              <Chip 
                                label={conferenceHotel.hasBusParking ? 'Yes' : 'No'}
                                color={conferenceHotel.hasBusParking ? 'success' : 'default'}
                                size="small"
                              />
                            </Box>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                Transit to Venue Available
                              </Typography>
                              <Chip 
                                label={conferenceHotel.hasTransitToVenue ? 'Yes' : 'No'}
                                color={conferenceHotel.hasTransitToVenue ? 'success' : 'default'}
                                size="small"
                              />
                            </Box>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                Credit Card Required
                              </Typography>
                              <Chip 
                                label={conferenceHotel.requiresCreditCard ? 'Yes' : 'No'}
                                color={conferenceHotel.requiresCreditCard ? 'warning' : 'default'}
                                size="small"
                              />
                            </Box>
                          </Grid>

                          <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="subtitle1" gutterBottom>
                              Tax Information
                            </Typography>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                Tax Per Room/Night
                              </Typography>
                              <Typography variant="body1">
                                {conferenceHotel.taxPerRoomNight ? formatCurrency(conferenceHotel.taxPerRoomNight) : 'Not set'}
                              </Typography>
                            </Box>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                Tax Per Room/Night (Non-Profit)
                              </Typography>
                              <Typography variant="body1">
                                {conferenceHotel.taxPerRoomNightNonProfit ? formatCurrency(conferenceHotel.taxPerRoomNightNonProfit) : 'Not set'}
                              </Typography>
                            </Box>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                Tax Rate Percentage
                              </Typography>
                              <Typography variant="body1">
                                {conferenceHotel.taxRatePercentage ? `${conferenceHotel.taxRatePercentage}%` : 'Not set'}
                              </Typography>
                            </Box>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                Tax Rate Percentage (Non-Profit)
                              </Typography>
                              <Typography variant="body1">
                                {conferenceHotel.taxRatePercentageNonProfit ? `${conferenceHotel.taxRatePercentageNonProfit}%` : 'Not set'}
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid size={{ xs: 12 }}>
                            <Divider sx={{ my: 3 }} />
                            <Typography variant="subtitle1" gutterBottom>
                              Contact & Administrative
                            </Typography>
                          </Grid>

                          <Grid size={{ xs: 12, md: 4 }}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                Contact Name
                              </Typography>
                              <Typography variant="body1">
                                {conferenceHotel.contactName || 'Not provided'}
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid size={{ xs: 12, md: 4 }}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                Contact Email
                              </Typography>
                              <Typography variant="body1">
                                {conferenceHotel.contactEmail || 'Not provided'}
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid size={{ xs: 12, md: 4 }}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                Contact Phone
                              </Typography>
                              <Typography variant="body1">
                                {conferenceHotel.contactPhone || 'Not provided'}
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid size={{ xs: 12 }}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                CSV File Send Date
                              </Typography>
                              <Typography variant="body1">
                                {conferenceHotel.dateToSendCSVFile 
                                  ? formatDate(conferenceHotel.dateToSendCSVFile) 
                                  : 'Not scheduled'
                                }
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid size={{ xs: 12 }}>
                            <Divider sx={{ my: 3 }} />
                            <Typography variant="subtitle1" gutterBottom>
                              Availability Dates
                            </Typography>
                          </Grid>

                          <Grid size={{ xs: 12, md: 6 }}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                Earliest Available Date
                              </Typography>
                              <Typography variant="body1">
                                {conferenceHotel.earliestAvailable 
                                  ? formatDate(conferenceHotel.earliestAvailable) 
                                  : 'Not set'
                                }
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid size={{ xs: 12, md: 6 }}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                Latest Available Date
                              </Typography>
                              <Typography variant="body1">
                                {conferenceHotel.latestAvailable 
                                  ? formatDate(conferenceHotel.latestAvailable) 
                                  : 'Not set'
                                }
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}
          </Box>
        )}

        {/* Groups Tab */}
        {mainTab === 1 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Conference Groups
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddGroup}
              >
                Add Group
              </Button>
            </Box>

            {groupsLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ ml: 2 }}>
                  Loading groups...
                </Typography>
              </Box>
            )}

            {groupsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {groupsError}
              </Alert>
            )}

            {!groupsLoading && conferenceGroups.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                No groups configured for this conference
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Group Name</strong></TableCell>
                      <TableCell align="center"><strong>Access Code</strong></TableCell>
                      <TableCell align="center"><strong>Logins After</strong></TableCell>
                      <TableCell align="center"><strong>A-Tier Allowed</strong></TableCell>
                      <TableCell align="center"><strong>A-Tier Reserved</strong></TableCell>
                      <TableCell align="center"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {conferenceGroups.map((group) => (
                      <TableRow key={group.documentId} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body1" fontWeight="medium">
                              {group.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {group.longName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={group.accessCode}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {group.allowLoginsAfterDateTime 
                              ? new Date(group.allowLoginsAfterDateTime).toLocaleString()
                              : 'No restriction'
                            }
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={group.aTierAllowed}
                            size="small"
                            color={group.aTierAllowed ? 'success' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="medium">
                            {group.aTierReserved || 0}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleEditGroup(group)}
                          >
                            Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Paper>

      {/* Add/Edit Group Dialog */}
      <EditGroupDialog
        open={showAddGroupDialog}
        onClose={handleCloseGroupDialog}
        conferenceId={conferenceId}
        group={editingGroup}
        onUpdate={handleGroupUpdated}
      />

      {/* Manage Hotels Dialog */}
      <Dialog 
        open={showAddHotelDialog} 
        onClose={handleCloseHotelDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Manage Conference Hotels</DialogTitle>
        <DialogContent>
          {hotelFormError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {hotelFormError}
            </Alert>
          )}
          
          <Tabs value={hotelDialogTab} onChange={(e, newValue) => setHotelDialogTab(newValue)} sx={{ mb: 3 }}>
            <Tab label="Add Existing Hotels" />
            <Tab label="Create New Hotel" />
          </Tabs>

          {hotelDialogTab === 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select existing hotels to add to this conference:
              </Typography>
              
              {availableHotels.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                  No available hotels found or all hotels are already associated with this conference
                </Typography>
              ) : (
                <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {availableHotels.map((hotel) => (
                    <ListItem key={hotel.documentId} disablePadding>
                      <ListItemButton 
                        onClick={() => {
                          setSelectedHotels(prev => 
                            prev.includes(hotel.documentId)
                              ? prev.filter(id => id !== hotel.documentId)
                              : [...prev, hotel.documentId]
                          );
                        }}
                      >
                        <Checkbox
                          checked={selectedHotels.includes(hotel.documentId)}
                          sx={{ ml: 2 }}
                        />
                        <ListItemText
                          primary={hotel.longName}
                          secondary={[
                            hotel.addressCity,
                            hotel.addressState
                          ].filter(Boolean).join(', ')}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}

          {hotelDialogTab === 1 && (
            <Box sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Hotel Name (Short)"
                    value={newHotelData.name}
                    onChange={(e) => setNewHotelData(prev => ({ ...prev, name: e.target.value }))}
                    fullWidth
                    required
                    placeholder="e.g., Marriott Downtown"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Hotel Name (Full)"
                    value={newHotelData.longName}
                    onChange={(e) => setNewHotelData(prev => ({ ...prev, longName: e.target.value }))}
                    fullWidth
                    required
                    placeholder="e.g., Downtown Marriott Hotel & Convention Center"
                  />
                </Grid>
                
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    label="City"
                    value={newHotelData.addressCity}
                    onChange={(e) => setNewHotelData(prev => ({ ...prev, addressCity: e.target.value }))}
                    fullWidth
                    placeholder="e.g., San Francisco"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    label="State"
                    value={newHotelData.addressState}
                    onChange={(e) => setNewHotelData(prev => ({ ...prev, addressState: e.target.value }))}
                    fullWidth
                    placeholder="e.g., CA"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    label="ZIP Code"
                    value={newHotelData.addressZip}
                    onChange={(e) => setNewHotelData(prev => ({ ...prev, addressZip: e.target.value }))}
                    fullWidth
                    placeholder="e.g., 94102"
                  />
                </Grid>
                
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Website"
                    value={newHotelData.website}
                    onChange={(e) => setNewHotelData(prev => ({ ...prev, website: e.target.value }))}
                    fullWidth
                    placeholder="https://www.hotel-website.com"
                  />
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Contact Name"
                    value={newHotelData.contactName}
                    onChange={(e) => setNewHotelData(prev => ({ ...prev, contactName: e.target.value }))}
                    fullWidth
                    placeholder="e.g., John Smith"
                  />
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Contact Email"
                    type="email"
                    value={newHotelData.contactEmail}
                    onChange={(e) => setNewHotelData(prev => ({ ...prev, contactEmail: e.target.value }))}
                    fullWidth
                    placeholder="contact@hotel.com"
                  />
                </Grid>
                
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Amenities"
                    value={newHotelData.amenities}
                    onChange={(e) => setNewHotelData(prev => ({ ...prev, amenities: e.target.value }))}
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="e.g., Pool, Fitness Center, Business Center, Free WiFi"
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseHotelDialog} disabled={hotelFormLoading}>
            Cancel
          </Button>
          {hotelDialogTab === 0 ? (
            <Button 
              onClick={handleAddExistingHotels} 
              variant="contained" 
              disabled={hotelFormLoading || selectedHotels.length === 0}
            >
              {hotelFormLoading ? <CircularProgress size={20} /> : `Add Selected Hotels (${selectedHotels.length})`}
            </Button>
          ) : (
            <Button 
              onClick={handleCreateNewHotel} 
              variant="contained" 
              disabled={hotelFormLoading || !newHotelData.name || !newHotelData.longName}
            >
              {hotelFormLoading ? <CircularProgress size={20} /> : 'Create & Add Hotel'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit Conference Dialog */}
      <EditConferenceDialog
        open={showEditConferenceDialog}
        onClose={handleCloseEditConferenceDialog}
        conference={conference}
        onUpdate={handleConferenceUpdated}
      />

      {/* Add Room Dialog */}
      <Dialog 
        open={showAddRoomDialog} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{isEditMode ? 'Edit Hotel Room' : 'Add New Hotel Room'}</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Select Hotel</InputLabel>
              <Select
                value={formData.conference_hotel}
                onChange={(e) => handleFormChange('conference_hotel', e.target.value)}
                label="Select Hotel"
              >
                {conferenceHotels.map((hotel) => (
                  <MenuItem key={hotel.documentId} value={hotel.documentId}>
                    {hotel.hotel.longName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Room Name"
              value={formData.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              fullWidth
              required
              placeholder="e.g., Standard Double Room"
            />

            <TextField
              label="Daily Rate"
              type="number"
              value={formData.dailyRate}
              onChange={(e) => handleFormChange('dailyRate', e.target.value)}
              fullWidth
              required
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              inputProps={{
                min: 0,
                step: 0.01
              }}
              placeholder="0.00"
            />

            <TextField
              label="Block Total"
              type="number"
              value={formData.blockTotal}
              onChange={(e) => handleFormChange('blockTotal', e.target.value)}
              fullWidth
              required
              inputProps={{
                min: 1
              }}
              placeholder="Number of rooms available"
              helperText="Total number of rooms in this block"
            />

            <TextField
              label="# of Beds"
              type="number"
              value={formData.bedCount}
              onChange={(e) => handleFormChange('bedCount', e.target.value)}
              fullWidth
              required
              inputProps={{
                min: 1,
                max: 2
              }}
              placeholder="1 or 2"
              helperText="Number of beds in the room (1 or 2)"
            />

            <TextField
              label="Max Occupants"
              type="number"
              value={formData.maxOccupants}
              onChange={(e) => handleFormChange('maxOccupants', e.target.value)}
              fullWidth
              inputProps={{
                min: 1
              }}
              placeholder="Optional"
              helperText="Maximum number of people per room (optional)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={formLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleFormSubmit} 
            variant="contained" 
            disabled={formLoading || !formData.name || !formData.dailyRate || !formData.blockTotal || !formData.conference_hotel}
          >
            {formLoading ? <CircularProgress size={20} /> : (isEditMode ? 'Update Room' : 'Add Room')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Conference Hotel Dialog */}
      {conference && (
        <EditConferenceHotelDialog
          open={showEditConferenceHotelDialog}
          onClose={handleCloseConferenceHotelDialog}
          conference={conference}
          conferenceHotel={editingConferenceHotel}
          onUpdate={handleConferenceHotelUpdated}
        />
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}